import { StateHandler } from "../../utils/StateHandler";
import { GameData } from "../../../game/server/data/GameData";
import { InitData } from '../../../game/server/data/InitData';

// PresentationState class to handle the state of the presentation
export class PresentationState extends StateHandler {
    public static readonly INACTIVE: number = 0;  // INACTIVE state
    public static readonly ACTIVE: number = 1;  // ACTIVE state
    constructor() { super(PresentationState.INACTIVE); }
}

// Abstract Presentation class
export abstract class Presentation {
    protected _gameData: GameData;
    protected _initData: InitData;
    // Core Timeline
    private _timeline: gsap.core.Timeline;

    // Core State
    private _presentationState: PresentationState = new PresentationState();

    // Setter for timeline, should only be used by the PresentationController
    public set timeline(tl: gsap.core.Timeline) {
        this._timeline = tl;
    }

    // Getter for timeline
    public get timeline(): gsap.core.Timeline {
        return this._timeline;
    }

    /**
     * Optional initialization method, can be defined by subclasses.
     * @param _flowState The state of the flow
     */
    public initialization?(_flowState: string): void;

    // Abstract method to create the presentation, must be implemented by subclasses
    public abstract createPresentation(_skipAble: Function): void;

    /**
     * Checks if the presentation is active.
     * @returns {boolean} True if active, otherwise false.
     */
    public get isActive(): boolean {
        return this._presentationState.value === PresentationState.ACTIVE;
    }

    /**
     * Checks if the presentation should be activated based on the provided data.
     * @param data Parsed game data
     * @returns {boolean} True if the presentation should be activated, otherwise false.
     */
    public checkForActivation(data: any): boolean {
        return true;
    }

    /**
     * Optional method to perform actions before the presentation starts, can be defined by subclasses.
     */
    public preStart?(): void;

    /**
     * Skips the current presentation timeline.
     */
    public skip(): void {
        if (this._timeline && this._timeline.isActive()) {
            this._timeline.totalProgress(1);
        }
    }

    /**
     * Sets the game data for the presentation.
     * @param data Parsed game data
     */
    public setGameData(data: any): void {
        this._gameData = data;
    }

    /**
     * Sets the initialization data for the presentation.
     * @param data Initialization server data
     */
    public setInitData(data: any): void {
        this._initData = data;
    }

    /**
     * Sets whether the presentation is playable.
     * @param val True if playable, otherwise false.
     */
    public setPlayable(val: boolean): void {
        this._presentationState.set(val ? PresentationState.ACTIVE : PresentationState.INACTIVE);
    }

    /**
     * Checks if the presentation is finished.
     * @returns {boolean} True if finished, otherwise false.
     */
    public isFinished(): boolean {
        return true;
    }

    /**
     * Optional method to perform actions when the presentation is done, can be defined by subclasses.
     */
    public done?(): void;
}